import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Chat as ChatIcon,
  Feed as FeedIcon,
  Settings as SettingsIcon,
  Assignment as ApplicationIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useAuth, useClubs } from '../context/AppContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`club-tabpanel-${index}`}
      aria-labelledby={`club-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const ClubDetailsPage: React.FC = () => {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clubs } = useClubs();

  const [activeTab, setActiveTab] = useState(0);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  const club = clubs.find(c => c.id === clubId);

  // Check membership status
  const isUserMember = club?.members?.some((member: any) => member.user === user?.id) ||
                       user?.clubs?.some((userClub: any) => userClub.club?.id === clubId);

  // Debug membership status
  // console.log('Debug - Club membership:', {
  //   clubId,
  //   userId: user?.id,
  //   isUserMember,
  //   clubMembers: club?.members,
  //   userClubs: user?.clubs
  // });

  // Get user's role in this club
  // Handle both formats: club as string ID or club as object with id property
  const userClubMembership = user?.clubs?.find((userClub: any) => {
    const clubIdInMembership = typeof userClub.club === 'string' ? userClub.club : userClub.club?.id;
    return clubIdInMembership === clubId;
  });
  const memberRole = userClubMembership?.memberRole;

  // Also check if user's role from the club data itself
  const memberRoleFromClub = club?.memberRole;

  // Use canManage flag from backend response (preferred)
  // Fallback to local calculation if not provided
  const canManageClub = club?.canManage !== undefined ? club.canManage : (() => {
    // Fallback: Check if user is club mentor/creator (can manage club)
    const hasManageClubsPermission = user?.permissions?.canManageClubs === true;
    const facultyMentorId = (club as any)?.facultyMentorId;
    const createdById = club?.createdBy;

    return (
      (user?.role === 'Faculty' || (user?.role === 'Ambassador' && hasManageClubsPermission)) &&
      (
        facultyMentorId === user?.id ||
        createdById === user?.id ||
        memberRole === 'President' ||
        memberRole === 'Vice President' ||
        memberRoleFromClub === 'President' ||
        memberRoleFromClub === 'Vice President'
      )
    );
  })();

  // Debug logging
  console.log('🔍 ClubDetailsPage - canManageClub:', {
    clubId,
    userId: user?.id,
    canManageFromBackend: club?.canManage,
    canManageCalculated: canManageClub,
    memberRole,
    createdBy: club?.createdBy
  });



  const handleJoinClub = () => {
    if (!club) return;

    if (isUserMember) {
      alert('You are already a member of this club!');
      return;
    }

    // Navigate to quiz page or show join dialog
    setJoinDialogOpen(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    console.log('Tab change triggered:', newValue, 'Current tab:', activeTab);
    setActiveTab(newValue);
  };



  if (!club) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Club not found</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: { xs: 'white', sm: '#f0f2f5' },
      pb: { xs: 10, sm: 2 }
    }}>
      {/* Mobile Header - Sticky */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 2, sm: 3 }
        }}>
          <Avatar
            sx={{
              width: { xs: 50, sm: 60 },
              height: { xs: 50, sm: 60 },
              fontSize: { xs: '1.5rem', sm: '1.8rem' },
              bgcolor: '#1877f2',
              border: '3px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}
          >
            {club.logo || '🏛️'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#1877f2',
                fontSize: { xs: '1.2rem', sm: '1.4rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {club.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {club.memberCount || club.members?.length || 0} members • {club.facultyMentor}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Main Content Container */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 680 },
        mx: 'auto',
        px: { xs: 0, sm: 2 },
        pt: { xs: 0, sm: 2 }
      }}>

      {/* Club Info Card - Mobile Optimized */}
      <Card sx={{
        mb: { xs: 1, sm: 2 },
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 2 },
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        border: { xs: 'none', sm: '1px solid #e4e6ea' }
      }}>
        <Box
          sx={{
            height: { xs: 150, sm: 200 },
            bgcolor: 'primary.light',
            backgroundImage: club.banner ? `url(${club.banner})` : 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-end',
            p: { xs: 2, sm: 3 }
          }}
        >
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.3) 100%)'
          }} />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                fontSize: { xs: '0.9rem', sm: '1rem' }
              }}
            >
              Welcome to {club.name}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.6,
              fontSize: { xs: '0.95rem', sm: '1rem' },
              mb: { xs: 2, sm: 3 },
              color: '#050505'
            }}
          >
            {club.description}
          </Typography>

          {/* Action Buttons */}
          {!isUserMember && (
            <>
              {club.applicationStatus ? (
                <Alert
                  severity={
                    club.applicationStatus.status === 'pending' ? 'warning' :
                    club.applicationStatus.status === 'approved' ? 'success' : 'error'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Application {club.applicationStatus.status.charAt(0).toUpperCase() + club.applicationStatus.status.slice(1)}
                  </Typography>
                  <Typography variant="body2">
                    Quiz Score: {club.applicationStatus.percentage}% • Applied on {new Date(club.applicationStatus.appliedAt).toLocaleDateString()}
                  </Typography>
                  {club.applicationStatus.reviewedAt && (
                    <Typography variant="body2">
                      Reviewed on {new Date(club.applicationStatus.reviewedAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleJoinClub}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    borderRadius: { xs: 3, sm: 2 },
                    py: { xs: 1.5, sm: 1 },
                    fontSize: { xs: '1rem', sm: '0.95rem' },
                    fontWeight: 600,
                    backgroundColor: '#1877f2',
                    mb: 2,
                    '&:hover': {
                      backgroundColor: '#166fe5'
                    }
                  }}
                >
                  Join Club
                </Button>
              )}
            </>
          )}

          {isUserMember && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 2,
              p: { xs: 1.5, sm: 1 },
              borderRadius: { xs: 3, sm: 2 },
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50'
            }}>
              <Chip
                label={memberRole || 'Member'}
                color="success"
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.75rem' }
                }}
              />
              <Typography
                variant="body2"
                color="success.main"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '0.85rem' }
                }}
              >
                You're a member of this club
              </Typography>
            </Box>
          )}

          {/* Manage Club Button for Faculty */}
          {canManageClub && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate(`/clubs/${clubId}/manage`)}
              fullWidth
              sx={{
                textTransform: 'none',
                borderRadius: { xs: 3, sm: 2 },
                py: { xs: 1.5, sm: 1 },
                fontSize: { xs: '1rem', sm: '0.95rem' },
                fontWeight: 600,
                mb: 2,
                borderColor: '#1877f2',
                color: '#1877f2',
                '&:hover': {
                  borderColor: '#166fe5',
                  backgroundColor: 'rgba(24, 119, 242, 0.04)'
                }
              }}
            >
              Manage Club
            </Button>
          )}

          {/* Club Stats */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 2, sm: 3 },
            p: { xs: 2, sm: 2.5 },
            backgroundColor: '#f8f9fa',
            borderRadius: { xs: 2, sm: 1.5 },
            border: '1px solid #e4e6ea'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1877f2',
                  fontSize: { xs: '1.2rem', sm: '1.3rem' },
                  mb: 0.5
                }}
              >
                {club.memberCount || club.members?.length || 0}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              >
                Members
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#1877f2',
                  fontSize: { xs: '1.2rem', sm: '1.3rem' },
                  mb: 0.5
                }}
              >
                -
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              >
                Posts
              </Typography>
            </Box>
            <Box sx={{
              textAlign: 'center',
              gridColumn: { xs: '1 / -1', sm: 'auto' }
            }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: '#666',
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  mb: 0.5
                }}
              >
                Mentor
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.85rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {club.facultyMentor}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <Card sx={{
        mb: { xs: 1, sm: 2 },
        mx: { xs: 0, sm: 0 },
        borderRadius: { xs: 0, sm: 2 },
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
        border: { xs: 'none', sm: '1px solid #e4e6ea' }
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="club tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-flexContainer': {
              minHeight: { xs: 64, sm: 56 }
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              minHeight: { xs: 64, sm: 56 },
              py: { xs: 1.5, sm: 1 },
              minWidth: { xs: 80, sm: 100 },
              '&.Mui-selected': {
                color: '#1877f2'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1877f2'
            }
          }}
        >
          {/* Build tabs array dynamically */}
          {(() => {
            const tabs = [];
            let tabIndex = 0;

            // Non-members: About only
            if (!isUserMember) {
              tabs.push(
                <Tab
                  key="about"
                  icon={<GroupsIcon fontSize="small" />}
                  label="About"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
              tabIndex++;
            }

            // Members with management rights
            else if (isUserMember && canManageClub) {
              tabs.push(
                <Tab
                  key="manage"
                  icon={<SettingsIcon fontSize="small" />}
                  label="Manage"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
              tabIndex++;

              tabs.push(
                <Tab
                  key="applications"
                  icon={<ApplicationIcon fontSize="small" />}
                  label="Applications"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
              tabIndex++;

              tabs.push(
                <Tab
                  key="feed"
                  icon={<FeedIcon fontSize="small" />}
                  label="Feed"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
              tabIndex++;

              tabs.push(
                <Tab
                  key="discussions"
                  icon={<ChatIcon fontSize="small" />}
                  label="Discussions"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
            }

            // Regular members
            else if (isUserMember) {
              tabs.push(
                <Tab
                  key="member-area"
                  icon={<DashboardIcon fontSize="small" />}
                  label="Member Area"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
              tabIndex++;

              tabs.push(
                <Tab
                  key="about"
                  icon={<GroupsIcon fontSize="small" />}
                  label="About"
                  iconPosition="top"
                  id={`club-tab-${tabIndex}`}
                  aria-controls={`club-tabpanel-${tabIndex}`}
                />
              );
            }

            return tabs;
          })()}
        </Tabs>
      </Card>

      {/* Tab Panels */}
      {!isUserMember && (
        <>
          <TabPanel value={activeTab} index={0}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  About {club?.name}
                </Typography>

                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  {club?.description || 'No description available.'}
                </Typography>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Club Members ({club.memberCount || club.members?.length || 0})
                </Typography>

                {club.members && club.members.length > 0 ? (
                  <List>
                    {club.members.slice(0, 5).map((member: any, index: number) => (
                      <ListItem key={member.id || index}>
                        <ListItemAvatar>
                          <Avatar>
                            {member.profilePicture || member.name?.charAt(0) || '👤'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.name || 'Unknown Member'}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={member.role || 'Member'}
                                size="small"
                                color={member.role === 'President' ? 'error' :
                                       member.role === 'Vice President' ? 'warning' : 'primary'}
                                variant="outlined"
                              />
                              {member.branch && (
                                <Typography variant="caption" color="text.secondary">
                                  {member.branch}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                    {club.members.length > 5 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        And {club.members.length - 5} more members...
                      </Typography>
                    )}
                  </List>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No members found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </TabPanel>

        </>
      )}

      {isUserMember && (
        <>
          {canManageClub && (
            <>
              {/* Club Management Tab */}
              <TabPanel value={activeTab} index={0}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                      ⚙️
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Club Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Manage club settings, quiz questions, and details
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate(`/clubs/${clubId}/manage?tab=0`)}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#1877f2',
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#166fe5'
                        }
                      }}
                    >
                      Manage Club Details
                    </Button>
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Applications Tab */}
              <TabPanel value={activeTab} index={1}>
                <Card>
                  <CardContent sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>
                      📋
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Club Applications
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Review and manage membership applications for this club
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate(`/clubs/${clubId}/manage?tab=2`)}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#1877f2',
                        px: 4,
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: '#166fe5'
                        }
                      }}
                    >
                      View Club Applications
                    </Button>
                  </CardContent>
                </Card>
              </TabPanel>
            </>
          )}

          {/* Member Area Tab */}
          <TabPanel value={activeTab} index={canManageClub ? 2 : 0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Welcome Section */}
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1877f2' }}>
                    Welcome to {club?.name} Member Area! 🎉
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Access exclusive member content, connect with fellow members, and stay updated with club activities.
                  </Typography>
                </CardContent>
              </Card>

              {/* Member Features Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                {/* Insider Feed Card */}
                <Card sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h1" sx={{ fontSize: '3rem', mb: 2 }}>
                      📱
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Insider Feed
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Connect with club members and stay updated with exclusive content
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/clubs/${clubId}/feed`)}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#1877f2',
                        '&:hover': { backgroundColor: '#166fe5' }
                      }}
                    >
                      Open Feed
                    </Button>
                  </CardContent>
                </Card>

                {/* Discussions Card */}
                <Card sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h1" sx={{ fontSize: '3rem', mb: 2 }}>
                      💬
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Discussions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Engage in meaningful conversations with your club members
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => navigate(`/clubs/${clubId}/discussions`)}
                      sx={{
                        textTransform: 'none',
                        backgroundColor: '#1877f2',
                        '&:hover': { backgroundColor: '#166fe5' }
                      }}
                    >
                      View Discussions
                    </Button>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </TabPanel>

          {/* About Tab for Members */}
          <TabPanel value={activeTab} index={canManageClub ? 3 : 1}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  About {club?.name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6 }}>
                  {club?.description || 'No description available.'}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Club Members ({club.memberCount || club.members?.length || 0})
                </Typography>
                {club.members && club.members.length > 0 ? (
                  <List>
                    {club.members.slice(0, 8).map((member: any, index: number) => (
                      <ListItem key={member.id || index}>
                        <ListItemAvatar>
                          <Avatar>
                            {member.profilePicture || member.name?.charAt(0) || '👤'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.name || 'Unknown Member'}
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={member.role || 'Member'}
                                size="small"
                                color={member.role === 'President' ? 'error' :
                                       member.role === 'Vice President' ? 'warning' : 'primary'}
                                variant="outlined"
                              />
                              {member.branch && (
                                <Typography variant="caption" color="text.secondary">
                                  {member.branch}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                    {club.members.length > 8 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                        And {club.members.length - 8} more members...
                      </Typography>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No members found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </TabPanel>
        </>
      )}

      {/* Join Club Dialog */}
      <Dialog open={joinDialogOpen} onClose={() => setJoinDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Join {club.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Ready to become part of {club.name}? You'll need to pass our entry quiz with at least 70% to join.
          </Typography>
          <Alert severity="info">
            The quiz contains {Array.isArray(club.quiz) ? club.quiz.length : 0} questions about the club and related topics.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJoinDialogOpen(false)}>Maybe Later</Button>
          <Button variant="contained" onClick={() => {
            setJoinDialogOpen(false);
            navigate(`/clubs/${clubId}/quiz`);
          }}>
            Start Quiz
          </Button>
        </DialogActions>
      </Dialog>



      </Box>
    </Box>
  );
};



export default ClubDetailsPage;